import { sprintf } from 'sprintf-js';

import { BarChart } from 'modules/salaries/types/BarChart';
import { BarData } from 'modules/salaries/types/BarData';
import { EstimatedRange } from 'modules/salaries/types/EstimatedRange';
import { LineChart } from 'modules/salaries/types/LineChart';
import { SubmissionData } from 'modules/salaries/types/SubmissionData';
import { sum } from 'utils/functional';
import { range } from 'utils/functional/array/range';

import { buildXTicks } from './buildXTicks';

export function buildMainChart({
  submissionData,
  estimatedRange,
}: {
  submissionData: SubmissionData;
  estimatedRange: EstimatedRange;
}) {
  const { histogramData } = submissionData || {};
  const { scatterData, sectionLimits } = estimatedRange || {};
  let animationIndex = 0;
  let totalCount = 0;

  // Normalize scatter values to the bar chart values
  if (histogramData && scatterData) {
    scatterData.y = scatterData.y.map(
      (v: number) => v * Math.max(...histogramData.y),
    );
  }

  if (
    histogramData &&
    scatterData &&
    scatterData.x[0] < histogramData.xEdges[0]
  ) {
    const xEdgeInterval = histogramData.xEdges[1] - histogramData.xEdges[0];
    const newXEdges = [histogramData.xEdges[0] - xEdgeInterval];
    const newX = [histogramData.x[0] - xEdgeInterval];
    while (newXEdges[newXEdges.length - 1] > scatterData.x[0]) {
      newXEdges.push(newXEdges[newXEdges.length - 1] - xEdgeInterval);
      newX.push(newX[newX.length - 1] - xEdgeInterval);
    }
    const newY = new Array(newX.length).fill(0);

    histogramData.xEdges = [...newXEdges.reverse(), ...histogramData.xEdges];
    histogramData.x = [...newX.reverse(), ...histogramData.x];
    histogramData.y = [...newY, ...histogramData.y];
  }

  if (
    histogramData &&
    scatterData &&
    scatterData.x[scatterData.x.length - 1] >
      histogramData.xEdges[histogramData.xEdges.length - 1]
  ) {
    const xEdgeInterval = histogramData.xEdges[1] - histogramData.xEdges[0];
    const newXEdges = [
      histogramData.xEdges[histogramData.xEdges.length - 1] + xEdgeInterval,
    ];
    const newX = [histogramData.x[histogramData.x.length - 1] + xEdgeInterval];
    while (
      newXEdges[newXEdges.length - 1] < scatterData.x[scatterData.x.length - 1]
    ) {
      newXEdges.push(newXEdges[newXEdges.length - 1] + xEdgeInterval);
      newX.push(newX[newX.length - 1] + xEdgeInterval);
    }
    const newY = new Array(newX.length).fill(0);

    histogramData.xEdges = [...histogramData.xEdges, ...newXEdges];
    histogramData.x = [...histogramData.x, ...newX];
    histogramData.y = [...histogramData.y, ...newY];
  }

  if (
    histogramData &&
    scatterData &&
    Math.floor(Math.max(...scatterData.y)) >
      Math.floor(Math.max(...histogramData.y))
  ) {
    scatterData.y = scatterData.y.map(
      (d: number) =>
        d * (Math.max(...histogramData.y) / Math.max(...scatterData.y)),
    );
  }

  let lineChart: LineChart | null = null;
  let barChart: BarChart | null = null;
  if (histogramData) {
    const barData: BarData[] = [];
    histogramData.x.forEach((amount: number, i: number) => {
      barData.push({
        animationIndex: histogramData.y[i] > 0 ? animationIndex : undefined,
        amount,
        count: histogramData.y[i],
        salaryRange: `${histogramData.xEdges[i].toLocaleString(CURRENT_LOCALE, {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 0,
        })}-${histogramData.xEdges[i + 1].toLocaleString(CURRENT_LOCALE, {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 0,
        })}`,
        numberOfSubmissions: sprintf(getText('%(count)s %(salaryLabel)s'), {
          count: histogramData.y[i],
          salaryLabel: histogramData.y[i] === 1 ? 'salary' : 'salaries',
        }),
      });

      if (histogramData.y[i] > 0) {
        animationIndex += 1;
      }
    });

    const maxCount = barData.reduce(
      (acc, salary) => Math.max(acc, salary.count),
      0,
    );

    totalCount = sum(histogramData.y);

    const xRangeMin = histogramData.xEdges[0];
    const xRangeMax = histogramData.xEdges[histogramData.xEdges.length - 1];

    const MAX_GRID_LINES = 5;
    const yRangeMin = 0;
    const yRangeMax = maxCount < 6 ? maxCount : Math.ceil(maxCount / 5) * 5;
    const yGridLines = Math.min(yRangeMax, MAX_GRID_LINES);
    const tickInterval = yRangeMax / yGridLines;
    // Add one to include max value in range
    const submissionsRange = range(yRangeMin, yRangeMax + 1, tickInterval);

    let yAxisBarTicks = [];
    const numberOfTicks = 3;
    if (submissionsRange.length === 5) {
      // Edge case for max submissions of 4 (e.g. 4 grid lines)
      // Drop the `0` and return all other values
      yAxisBarTicks = [
        submissionsRange[4],
        submissionsRange[2],
        submissionsRange[0],
      ];
    } else if (submissionsRange.length > numberOfTicks) {
      yAxisBarTicks = [submissionsRange[submissionsRange.length - 1]];
      const middleRange = submissionsRange.slice(1, -1);
      const interval = middleRange.length / numberOfTicks;
      const middleTicks = numberOfTicks - 2;

      for (let i = 0; i < middleTicks; i++) {
        const evenIndex = Math.round(middleTicks - i * interval + interval / 2);
        yAxisBarTicks.push(middleRange[evenIndex]);
      }

      yAxisBarTicks.push(submissionsRange[1]);
    } else {
      yAxisBarTicks = submissionsRange.reverse();
    }

    barChart = {
      name: 'submissions',
      active: false,
      xRangeMin,
      xRangeMax,
      yRangeMin,
      yRangeMax,
      yAxisLabel: getText('Number of Submissions'),
      yAxisTicks: yAxisBarTicks,
      data: barData,
      xGridLines: 11,
      yGridLines,
    };
  }

  if (scatterData) {
    const xRangeMin = Math.round(scatterData.x[0] / 1000) * 1000;
    const xRangeMax =
      Math.round(scatterData.x[scatterData.x.length - 1] / 1000) * 1000;
    const yGridLines = Math.min(xRangeMax, 6);
    lineChart = {
      name: 'estimatedRange',
      active: false,
      sectionLimits,
      xRangeMin,
      xRangeMax,
      yRangeMin: 0,
      yRangeMax: Math.max(...scatterData.y),
      yGridLines,
      xGridLines: 11,
      points: scatterData.x.map((_: number, i: number) => [
        scatterData.x[i],
        scatterData.y[i],
      ]),
    };
  }

  if (barChart && lineChart) {
    barChart.active = totalCount >= 100;
    lineChart.active = totalCount < 100;
  }

  if (barChart && !lineChart) {
    barChart.active = true;
  }

  if (lineChart && !barChart) {
    lineChart.active = true;
  }

  const xTicks = buildXTicks(barChart, lineChart);

  return { xTicks, lineChart, barChart };
}
